import axios, { AxiosError } from "axios";
import { ROUTES_MAP } from "../routes/routesMap";
import { TOKEN_KEY } from "../constants/constants";
import { ApiError } from "../i18n/errorCodes";
import { configuredFrontendPath } from "../routes/frontendBasePath";

declare module "axios" {
  interface AxiosRequestConfig {
    /** The auth restore thunk owns its request fence and 401 side effects. */
    deferUnauthorized?: boolean;
  }
}

const baseURL = import.meta.env.VITE_API_URL;

export const getToken = () =>
  typeof window !== "undefined" && localStorage.getItem(TOKEN_KEY)
    ? localStorage.getItem(TOKEN_KEY)
    : null;

export const getAuthorizationHeader = () => `Bearer ${getToken()}`;

/** Shared invalid-session behavior; auth restore applies it only for the winning request. */
export function invalidateSession(): void {
  window.localStorage.setItem(
    "lastLocation",
    window.location.pathname + window.location.search
  );
  window.localStorage.removeItem(TOKEN_KEY);
  window.location.href = configuredFrontendPath(ROUTES_MAP.auth.login);
}

export const axiosInstance = axios.create({
  baseURL,
  headers: { Authorization: getAuthorizationHeader() },
});

axiosInstance.interceptors.request.use((config) => {
  // Bound snapshot reads without imposing a transfer deadline on uploads.
  if (config.method === "get" && !config.timeout) config.timeout = 30000;
  const token = getAuthorizationHeader();
  if (token) {
    config.headers.Authorization = token;
  }
  config.headers["x-app-version"] = 3;
  return config;
});

/** Normalises any failed api response into an ApiError the toasts understand. */
export function toApiError(error: AxiosError): ApiError {
  const data = error.response?.data as
    | {
        code?: string;
        error?: string;
        detail?: string;
        message?: string | string[];
        details?: Record<string, unknown>;
        playlistTier?: string;
        mediaTier?: string;
      }
    | undefined;
  const status = error.response?.status ?? 0;

  // Treat `error` as a code when it looks like an UPPER_SNAKE_CASE token
  // (the convention used by the api's HttpException bodies from Phase 8).
  const codeFromError =
    data?.error && /^[A-Z][A-Z0-9_]*$/.test(data.error)
      ? data.error
      : undefined;
  // A25: throttler 429s (and proxy 429s) carry no code.
  const code =
    data?.code ?? codeFromError ?? (status === 429 ? "RATE_LIMITED" : undefined);

  // A26: an older validation body sends `message` as an array.
  const rawMessage = data?.message;
  const message = Array.isArray(rawMessage)
    ? rawMessage.filter((part) => typeof part === "string").join(". ")
    : rawMessage;

  const fallback =
    data?.detail ||
    message ||
    (codeFromError ? undefined : data?.error) ||
    error.message ||
    "Something went wrong";

  // Typed api errors carry `{ code, message, details }`; forward `details`
  // verbatim so toasts can interpolate bytesAvailable / feature / reason.
  // The legacy tier fields stay for the CROSS_TIER_MEDIA copy.
  const rawDetails =
    data?.details && typeof data.details === "object"
      ? (data.details as Record<string, unknown>)
      : undefined;
  const details =
    rawDetails || data?.playlistTier || data?.mediaTier
      ? {
          ...(rawDetails ?? {}),
          playlistTier: data?.playlistTier,
          mediaTier: data?.mediaTier,
        }
      : undefined;

  return new ApiError(code, fallback, status, details);
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error?.response?.status === 401) {
      // Restoration owns its request fence. Other requests retain immediate 401 routing.
      if (!error.config?.deferUnauthorized) invalidateSession();
    }
    throw toApiError(error);
  }
);
